/**
 * Keycloak Device Authorization Grant (RFC 8628) for the store CLI: a public
 * device-flow client (`skaile-store-cli`) issues a plain realm JWT the store's
 * `AuthGuard` accepts — a CLI can't use the storefront's confidential browser
 * client. Tokens cache at `~/.skaile/auth.json` (0600, shared with `store/cli`);
 * `getAccessToken` refreshes transparently. All targets are env-overridable.
 *
 * @docLink cli/dev-guide#store-auth
 */
import { createHash, randomBytes } from "node:crypto";
import { chmodSync, mkdirSync, readFileSync, rmSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

/**
 * A PKCE (RFC 7636) `code_verifier` + its S256 `code_challenge`. Keycloak
 * enforces PKCE on the `skaile-store-cli` device flow (the device-authorization
 * request 400s with "Missing parameter: code_challenge_method" otherwise), so
 * every `deviceLogin` mints a fresh pair: the challenge goes on the device-auth
 * request, the verifier on the matching token poll. `base64url` (no padding)
 * per the spec.
 */
function makePkcePair(): { verifier: string; challenge: string } {
  const verifier = randomBytes(32).toString("base64url");
  const challenge = createHash("sha256").update(verifier).digest("base64url");
  return { verifier, challenge };
}

/** Resolved auth targets — every field has a prod default, all env-overridable. */
export interface StoreAuthConfig {
  keycloakUrl: string;
  realm: string;
  clientId: string;
  authFile: string;
}

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

/**
 * Load the auth config. Defaults target the production store
 * (`auth.skaile.ai`, realm `skaile-store`, public client `skaile-store-cli`);
 * override with `SKAILE_KEYCLOAK_URL` / `SKAILE_STORE_REALM` /
 * `SKAILE_STORE_CLI_CLIENT` / `SKAILE_AUTH_FILE` for dev or a preview deploy.
 */
export function loadStoreAuthConfig(): StoreAuthConfig {
  const home = process.env.SKAILE_HOME ?? join(homedir(), ".skaile");
  return {
    keycloakUrl: trimTrailingSlash(process.env.SKAILE_KEYCLOAK_URL ?? "https://auth.skaile.ai"),
    realm: process.env.SKAILE_STORE_REALM ?? "skaile-store",
    clientId: process.env.SKAILE_STORE_CLI_CLIENT ?? "skaile-store-cli",
    authFile: process.env.SKAILE_AUTH_FILE ?? join(home, "auth.json"),
  };
}

/** Keycloak OIDC endpoints derived from the realm base. */
function oidc(config: StoreAuthConfig): { deviceAuth: string; token: string } {
  const base = `${config.keycloakUrl}/realms/${config.realm}/protocol/openid-connect`;
  return { deviceAuth: `${base}/auth/device`, token: `${base}/token` };
}

export interface StoredAuth {
  access_token: string;
  refresh_token?: string;
  /** Epoch ms when the access token expires (with a safety margin already applied). */
  expires_at: number;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface DeviceAuthResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete?: string;
  interval?: number;
  expires_in: number;
}

/** Refresh this many ms before real expiry so an in-flight call never 401s. */
const EXPIRY_MARGIN_MS = 30_000;

/** Injectable seams so the poll loop is unit-testable without real time or network. */
export interface AuthDeps {
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  /** Called once with the verification URL + user code (default: write to stderr). */
  onPrompt?: (url: string, userCode: string) => void;
}

function readAuth(config: StoreAuthConfig): StoredAuth | null {
  try {
    const parsed = JSON.parse(readFileSync(config.authFile, "utf-8"));
    // Reject a corrupt / hand-edited file rather than treat it as a session.
    if (parsed && typeof parsed.access_token === "string") return parsed as StoredAuth;
    return null;
  } catch {
    return null;
  }
}

/**
 * The caller's store identity, decoded LOCALLY from the cached token's JWT
 * payload (no network, no signature check — this is an author-side hint, not an
 * authorization decision). Returns `null` when not signed in or the token is
 * unreadable. `githubLogin` is the verified GitHub handle (the preferred
 * publisher namespace); `username` is the Keycloak `preferred_username`.
 */
export function getStoreIdentity(
  config: StoreAuthConfig = loadStoreAuthConfig(),
): { username?: string; githubLogin?: string } | null {
  const auth = readAuth(config);
  if (!auth) return null;
  try {
    const payload = auth.access_token.split(".")[1];
    const json = JSON.parse(Buffer.from(payload, "base64url").toString("utf-8"));
    const username = typeof json.preferred_username === "string" ? json.preferred_username : undefined;
    const githubLogin = typeof json.github_login === "string" ? json.github_login : undefined;
    return username || githubLogin ? { username, githubLogin } : null;
  } catch {
    return null;
  }
}

function writeAuth(config: StoreAuthConfig, auth: StoredAuth): void {
  mkdirSync(dirname(config.authFile), { recursive: true });
  writeFileSync(config.authFile, JSON.stringify(auth, null, 2), { mode: 0o600 });
  chmodSync(config.authFile, 0o600);
}

/** Remove the cached session (sign out). */
export function logout(config: StoreAuthConfig): void {
  try {
    rmSync(config.authFile);
  } catch {
    // already absent — nothing to do
  }
}

/** True when a cached, well-formed session exists (validity/refresh resolved on use). */
export function hasStoredAuth(config: StoreAuthConfig): boolean {
  return readAuth(config) !== null;
}

function tokenToStored(token: TokenResponse): StoredAuth {
  return {
    access_token: token.access_token,
    refresh_token: token.refresh_token,
    expires_at: Date.now() + (token.expires_in ?? 300) * 1000 - EXPIRY_MARGIN_MS,
  };
}

const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function defaultPrompt(url: string, userCode: string): void {
  process.stderr.write(
    `\nTo sign in, open:\n\n    ${url}\n\nand enter the code: ${userCode}\n\nWaiting for approval…\n`,
  );
}

/**
 * Run the device flow to completion, persisting tokens on success. Prints the
 * verification URL + user code for the operator to approve in a browser.
 */
export async function deviceLogin(
  config: StoreAuthConfig,
  deps: AuthDeps = {},
): Promise<StoredAuth> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const sleep = deps.sleep ?? defaultSleep;
  const onPrompt = deps.onPrompt ?? defaultPrompt;
  const endpoints = oidc(config);
  const pkce = makePkcePair();

  const deviceRes = await fetchImpl(endpoints.deviceAuth, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: config.clientId,
      scope: "openid profile",
      code_challenge: pkce.challenge,
      code_challenge_method: "S256",
    }),
  });
  if (!deviceRes.ok) {
    throw new Error(
      `Device authorization request failed (${deviceRes.status}): ${await deviceRes.text()}`,
    );
  }
  const device = (await deviceRes.json()) as DeviceAuthResponse;

  onPrompt(device.verification_uri_complete ?? device.verification_uri, device.user_code);

  const intervalMs = (device.interval ?? 5) * 1000;
  const deadline = Date.now() + device.expires_in * 1000;
  let waitMs = intervalMs;

  while (Date.now() < deadline) {
    await sleep(waitMs);
    const tokenRes = await fetchImpl(endpoints.token, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "urn:ietf:params:oauth:grant-type:device_code",
        device_code: device.device_code,
        client_id: config.clientId,
        code_verifier: pkce.verifier,
      }),
    });
    const token = (await tokenRes.json()) as TokenResponse;
    if (tokenRes.ok) {
      const stored = tokenToStored(token);
      writeAuth(config, stored);
      return stored;
    }
    // RFC 8628 §3.5 pending / slow-down handling.
    if (token.error === "authorization_pending") continue;
    if (token.error === "slow_down") {
      waitMs += 5_000;
      continue;
    }
    throw new Error(`Sign-in failed: ${token.error_description ?? token.error ?? "unknown error"}`);
  }
  throw new Error("Sign-in timed out before approval.");
}

/** Exchange a refresh token for a fresh access token; persist on success. */
async function refresh(
  config: StoreAuthConfig,
  refreshToken: string,
  fetchImpl: typeof fetch,
): Promise<StoredAuth> {
  const res = await fetchImpl(oidc(config).token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: config.clientId,
    }),
  });
  const token = (await res.json()) as TokenResponse;
  if (!res.ok) {
    throw new Error(
      `Token refresh failed: ${token.error_description ?? token.error ?? res.status}`,
    );
  }
  const stored = tokenToStored(token);
  writeAuth(config, stored);
  return stored;
}

/**
 * Return a valid access token, refreshing if it is expired (or about to). Throws
 * a clear "run `skaile store login`" error when there is no usable session.
 */
export async function getAccessToken(
  config: StoreAuthConfig,
  deps: AuthDeps = {},
): Promise<string> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const auth = readAuth(config);
  if (!auth) throw new Error("Not signed in. Run `skaile store login` first.");
  if (Date.now() < auth.expires_at) return auth.access_token;
  if (!auth.refresh_token) throw new Error("Session expired. Run `skaile store login` again.");
  try {
    return (await refresh(config, auth.refresh_token, fetchImpl)).access_token;
  } catch {
    throw new Error("Session expired and refresh failed. Run `skaile store login` again.");
  }
}
